import ApiService, { INTERVENERS_API_HOST } from './ApiService';

/**
 * @override
 */
class IntervenersService extends ApiService {
    /**
     * constructor
     */
    constructor() {
        super({ baseURL: INTERVENERS_API_HOST });
    }
    getIntervener(config, uuid) {
        return this.get(`/interveners/${uuid}`, config);
    }
    /**
     * @param {Object} config Axios config
     * @param {String} uuid uuid of the intervener
     * @return {Object} Promise
     */
    getIntervenerAccounts(config, uuid, offset = 0, limit = 20, showHideAccounts = false) {
        const uri = `/interveners/${uuid}/accounts?$limit=${limit}&$offset=${offset}&is_private_area=true${
            showHideAccounts ? '&show_hide_accounts=true' : ''
        }`;

        return this.get(uri, config);
    }

    getIntervenerAccountConfiguration(config, uuid, uuidAccount) {
        return this.get(`/interveners/${uuid}/accounts/${uuidAccount}/configuration`, config);
    }

    /**
     * @param {Object} config Axios config
     * @param {String} uuid uuid of the intervener
     * @param {String} type filter to select only movements, periodic orders or pending orders
     * @param {Integer} offset pagination offset
     * @param {Integer} limit pagination limit
     * @return {Object} Promise
     */
    getIntervenerOperations(config, uuid, type = '', offset = 0, limit = 25, exp = false) {
        return this.get(
            `/interveners/${uuid}/operations?$type=${type}&$limit=${limit}&$offset=${offset}&$export=${exp}&is_private_area=true`,
            config
        );
    }

    /**
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid uuid of the intervener
     * @return {Object} Promise
     */
    updateIntervenerData(config, body, uuid) {
        return this.patch(`/interveners/${uuid}`, body, config);
    }

    /**
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid uuid of the intervener
     * @param {String} uuidAddress uuid of the intervener address
     * @return {Object} Promise
     */
    updateIntervenerAddressData(config, body, uuid, uuidAddress) {
        return this.patch(`/interveners/${uuid}/addresses/${uuidAddress}`, body, config);
    }

    /**
     * @param {Object} config Axios config
     * @param {String} uuid uuid of the intervener
     */
    getIntervenerAddressData(config, uuid) {
        return this.get(`/interveners/${uuid}/addresses`, config);
    }

    /**
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid uuid of the intervener
     */
    uploadDocument(config, body, uuid) {
        return this.post(`/interveners/${uuid}/documents`, body, config);
    }

    /**
     * @param {Object} config Axios config
     * @param {String} uuid uuid of the intervener
     */
    getIntervenerDocuments(config, uuid) {
        return this.get(`/interveners/${uuid}/documents`, config);
    }

    /**
     * @param {String} uuid intervener UUID
     * @return {Object} Response
     */
    setConvenienceTest(uuid, payload) {
        return this.post(`/interveners/${uuid}/convenience-test`, payload);
    }

    getConvenienceTestScore(payload) {
        return this.post('/convenience-test-score', payload);
    }

    /**
     * Get intervener Documents
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener documents data
     */
    getIntervenerDocumentsValidations(config, uuid) {
        return this.getList(`/interveners/${uuid}/document-validations`, config);
    }

    update(intervener) {
        intervener.uuid_group = undefined;
        intervener.uuid_permission_set = undefined;
        intervener._links._self = undefined;
        intervener.permission_set = undefined;
        intervener.group = undefined;

        return this.put(`/interveners/${intervener.uuid}`, intervener);
    }

    /**
     * @override
     */
    upload(payload) {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };

        const formData = new FormData();
        for (let i = 0; i < payload.files.length; i++) {
            formData.append('attachment', payload.files[i]);
            formData.append('filename', payload.filesName[i]);
            formData.append(
                'category',
                typeof payload.category === 'string' ? payload.category : payload.category[i]
            );
            formData.append(
                'subcategory',
                typeof payload.subcategory === 'string'
                    ? payload.subcategory
                    : payload.subcategory[i]
            );
            formData.append('validated', payload.validated);
            payload.uuid_account ? formData.append('uuid_account', payload.uuid_account) : '';
        }
        return this.post(`/interveners/${payload.uuid}/documents`, formData, config);
    }

    /**
     * @param {Object} config Axios config
     * @param {String} uuid uuid of the intervener
     */
    getIntervenerBalance(config, uuid) {
        return this.get(`/interveners/${uuid}/balance?is_private_area=true`, config);
    }

    /**
     * @param {String} uuid Intervener UUID
     * @param {String} uuidDocument Document UUID
     * @return {Object} Response with the document
     */
    getDocument(uuid, uuidDocument) {
        return this.get(`/interveners/${uuid}/documents/${uuidDocument}`, {
            responseType: 'blob'
        }).then(res => {
            return res;
        });
    }

    getWealthReaderOperation(operationId) {
        return this.get(`/wealthreader/${operationId}`);
    }

    updateWealthReaderOperation(operationId, payload) {
        return this.patch(`/wealthreader/${operationId}`, payload);
    }

    /**
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid uuid of the intervener
     * @return {Object} Promise
     */
    updateIntervenerGdprData(config, body, uuid) {
        return this.patch(`/interveners/${uuid}/gdpr`, body, config);
    }

    /**
     * Updates CRS and FATCA data in a single request, generating only one document
     * even when both CRS and FATCA data are provided at the same time.
     *
     * Único punto de escritura: los antiguos PATCH /interveners/:uuid/crs y
     * /fatca se retiraron. El dato queda pendiente de bastantear por backoffice.
     *
     * @param {Object} config Axios config
     * @param {Object} body { crs, fatca, otp_verification }
     * @param {String} uuid uuid of the intervener
     * @return {Object} Promise
     */
    updateIntervenerCrsFatcaData(config, body, uuid) {
        return this.patch(`/interveners/${uuid}/crs-fatca`, body, config);
    }

    /**
     * Calls an external service to validate a TIN number
     * @param {Object} config Axios config
     * @param {String} countryCode Country code
     * @param {String} tinNumber Tin number
     * @param {String} [taxpayerType] Taxpayer type ('F' física / 'J' jurídica); only sent when provided
     * @return {Object} API Response
     */
    validateTinNumber(config, countryCode, tinNumber, taxpayerType) {
        const body = { countryCode, tinNumber };
        if (taxpayerType) {
            body.taxpayerType = taxpayerType;
        }
        return this.post('/tin-validator', body, config);
    }

    /**
     *
     * @param {*} uuid
     * @param {*} uuidDocument
     * @returns {Object} Promise
     */
    downloadIntervenerDocument(uuid, uuidDocument) {
        return this.get(`/interveners/${uuid}/documents/${uuidDocument}`, {
            responseType: 'blob'
        });
    }

    /**
     * Get intervener kyc data
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener kyc data
     */
    getIntervenerKyc(uuid, config) {
        return this.getList(`/interveners/${uuid}/kyc`, config);
    }

    /**
     * Get Max contribution limit
     * @param {String} uuid UUID
     * @param {Object} config Axios config
     * @return {Object} API Response
     */
    getMaxYearContribution(uuid, config) {
        return this.get(`/interveners/${uuid}/max-contribution`, config);
    }

    /**
     * Start Wealth Reader Oauth2 process
     * @param {*} payload uuid_intervener, uuid_account, product_type
     * @returns authUrl, operation_id
     */
    startWealthReaderOauth(payload) {
        return this.post('/wealthreader/oauth2', payload);
    }

    /**
     * Get intervener agent
     * @param {String} uuid UUID of agent user
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    getIntervenerAgent(uuid) {
        return this.get(`/agent/${uuid}`);
    }

    /**
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid uuid of the intervener
     * @return {Object} Promise
     */
    updateIntervenerHasAppEnableBiometricAccess(uuid, payload) {
        return this.patch(`/interveners/${uuid}/app/enableBiometricAccess`, payload);
    }

    /**
     * @param {String} uuid uuid of the intervener
     * @return {Object} Promise
     */
    getMissingCCCDocuments(uuid) {
        return this.get(`/interveners/${uuid}/ccc-documents`);
    }
    /**
     * @param {String} uuid uuid of the intervener
     * @return {Object} Promise
     */
    getIntervenerAllAssoiciatesDocuments(uuid) {
        return this.get(`/interveners/${uuid}/list-all-associated`);
    }

    getIntervenerClassByDocID(documentId) {
        return this.get(`/intervener-class/${documentId}`);
    }

    getRolesDefinition() {
        return this.get('/roles/definition');
    }
}

const intervenerService = new IntervenersService();

export default intervenerService;
